import { useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { SmartOLTLiveTopologyPort } from "../../../services/smartolt.service.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { filterOnus, nodeState, TOPOLOGY_COLORS } from "./smartoltTopologyUtils.ts"

export function SmartOLTPonTreeCanvas({
  oltName,
  ports,
  search,
}: {
  oltName: string
  ports: SmartOLTLiveTopologyPort[]
  search: string
}) {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [pathOnu, setPathOnu] = useState<string | null>(null)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [scale, setScale] = useState(1)
  const dragRef = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null)

  const visiblePorts = useMemo(
    () =>
      ports
        .map((port) => ({
          ...port,
          nodes: filterOnus(port.nodes, search),
        }))
        .filter((port) => !search.trim() || port.nodes.length > 0),
    [ports, search],
  )

  const onWheel = (event: React.WheelEvent) => {
    event.preventDefault()
    setScale((prev) => Math.min(1.8, Math.max(0.5, prev + (event.deltaY < 0 ? 0.08 : -0.08))))
  }

  const onMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return
    dragRef.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y }
  }

  const onMouseMove = (event: React.MouseEvent) => {
    if (!dragRef.current) return
    setOffset({
      x: dragRef.current.ox + (event.clientX - dragRef.current.x),
      y: dragRef.current.oy + (event.clientY - dragRef.current.y),
    })
  }

  const onMouseUp = () => {
    dragRef.current = null
  }

  const togglePort = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <div
      ref={containerRef}
      className="h-full w-full cursor-grab overflow-hidden bg-slate-50/60 active:cursor-grabbing"
      onWheel={onWheel}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
    >
      <div
        className="min-h-full min-w-full p-8"
        style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: "center top" }}
      >
        <div className="mx-auto flex max-w-[1200px] flex-col items-center gap-6">
          <div className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-900 shadow-xs">
            {oltName}
          </div>
          <div className="h-8 w-px bg-slate-300" />
          <div className="grid w-full gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visiblePorts.map((port) => {
              const open = expanded[port.key] ?? false
              const visibleNodes = open ? port.nodes : port.nodes.slice(0, 6)
              const hidden = Math.max(0, port.nodes.length - visibleNodes.length)
              return (
                <div key={port.key} className="rounded-2xl border border-slate-200/80 bg-white p-3.5 shadow-xs">
                  <button
                    type="button"
                    onClick={() => togglePort(port.key)}
                    className="flex w-full items-center justify-between gap-2 text-left"
                  >
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{port.label}</div>
                      <div className="text-[11px] text-slate-500">
                        {[port.pon_name, port.pon_type?.toUpperCase()].filter(Boolean).join(" · ") || "PON port"}
                      </div>
                    </div>
                    <span className="rounded-md bg-slate-100 border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700">
                      {port.online}/{port.onus} online
                    </span>
                  </button>
                  <div className="mt-3 space-y-1.5">
                    {visibleNodes.map((onu) => {
                      const state = nodeState(onu.status_kind)
                      const active = pathOnu === onu.onu_external_id
                      return (
                        <button
                          key={onu.onu_external_id}
                          type="button"
                          onClick={() => setPathOnu(onu.onu_external_id)}
                          onDoubleClick={() => navigate(SMARTOLT_PATHS.onuDetail(onu.onu_external_id))}
                          className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs transition-colors ${
                            active
                              ? "bg-blue-50 ring-1 ring-blue-500 text-blue-900"
                              : "bg-slate-50/80 hover:bg-slate-100 border border-slate-200/60"
                          }`}
                        >
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: TOPOLOGY_COLORS[state] }} />
                          <span className="min-w-0 flex-1 truncate text-slate-800 font-medium">{onu.name}</span>
                          <span className="text-[10px] uppercase font-semibold text-slate-500">{onu.status_kind}</span>
                        </button>
                      )
                    })}
                    {hidden > 0 ? (
                      <button
                        type="button"
                        onClick={() => togglePort(port.key)}
                        className="w-full rounded-lg bg-slate-100 hover:bg-slate-200/70 border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-700 transition-colors"
                      >
                        +{hidden} more
                      </button>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
